import {
  IPerioChart,
  isPerioSection,
  PERIO_SECTIONS,
  PerioChartSectionData,
  PerioSection,
} from '@dataTypes/chartingFeature.types'
import {
  clonePerioChartData,
  createEmptyPerioChartData,
  toStorageOrder,
} from './perio.utils'

export type VoiceCursor = {
  tooth: number
  section: PerioSection
  metric: 'PD' | 'GM' | 'CAL' | 'Mobility'
}
export interface PerioVoiceHighlight {
  tooth: number
  section: VoiceCursor['section']
  metric: keyof PerioChartSectionData
}
type Cell = {
  tooth: number
  section: VoiceCursor['section']
  metric: keyof PerioChartSectionData
}
/** Inclusive tooth range; supports ascending or descending. */
const toothRange = (from: number, to: number): number[] => {
  const step = from <= to ? 1 : -1
  const teeth: number[] = []
  for (let tooth = from; tooth !== to + step; tooth += step) teeth.push(tooth)
  return teeth
}
/**
 * Probe path: upper facial 1→16, upper lingual 16→1,
 * lower facial 32→17, lower lingual 17→32.
 */
const VOICE_SEQUENCE: Array<{ tooth: number; section: PerioSection }> = [
  ...toothRange(1, 16).map((tooth) => ({
    tooth,
    section: PerioSection.Facial,
  })),
  ...toothRange(16, 1).map((tooth) => ({
    tooth,
    section: PerioSection.Lingual,
  })),
  ...toothRange(32, 17).map((tooth) => ({
    tooth,
    section: PerioSection.Facial,
  })),
  ...toothRange(17, 32).map((tooth) => ({
    tooth,
    section: PerioSection.Lingual,
  })),
]
const shiftCursor = (
  cursor: VoiceCursor,
  step: 1 | -1,
): VoiceCursor | undefined => {
  const index = VOICE_SEQUENCE.findIndex(
    (entry) => entry.tooth === cursor.tooth && entry.section === cursor.section,
  )
  if (index < 0) return undefined
  const next = index + step
  if (next < 0 || next >= VOICE_SEQUENCE.length) return undefined
  const { tooth, section } = VOICE_SEQUENCE[next]
  return { ...cursor, tooth, section }
}
type Edit = Cell & { before: string; after: string }
type History = { cursor: VoiceCursor; ended: boolean; edits: Edit[] }
export type KeywordState = {
  cursor: VoiceCursor
  pending: number[]
  pendingBefore?: string
  paused: boolean
  ended: boolean
  history: History[]
}
export interface KeywordApplicationStep extends PerioVoiceHighlight {
  chart: IPerioChart
}
export const createKeywordState = (
  cursor: VoiceCursor = {
    tooth: 1,
    section: PerioSection.Facial,
    metric: 'PD',
  },
): KeywordState => ({
  cursor: { ...cursor },
  pending: [],
  paused: false,
  ended: false,
  history: [],
})
const numeric = ['PD', 'GM', 'CAL', 'Mobility']
const markerMetrics = ['Bleeding', 'Calculus', 'Supp', 'Furcation']
const metrics = [...numeric, ...markerMetrics]
const sites = ['distal', 'buccal', 'mesial']
const markers = ['D', 'B', 'M']
const read = (chart: IPerioChart, cell: Cell): string =>
  chart.perio_data?.[cell.section]?.[cell.metric]?.[cell.tooth - 1] ??
  (cell.metric === 'Mobility' ? '0' : '0 0 0')
const digit = (value: unknown): number => {
  if (
    typeof value !== 'number' ||
    !Number.isInteger(value) ||
    value < 0 ||
    value > 9
  )
    throw new Error(
      'Readings must be whole numbers from 0 to 9 in this editor. This phrase was not applied.',
    )
  return value
}
const object = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('Invalid voice command.')
  return value as Record<string, unknown>
}

/** Adjacent number operations are one spoken sequence, regardless of tool grouping. */
const coalesceNumbers = (input: unknown[]): Record<string, unknown>[] => {
  const result: Record<string, unknown>[] = []
  for (const command of input.map(object)) {
    if (
      command.op === 'numbers' &&
      Object.keys(command).every((field) => ['op', 'values'].includes(field)) &&
      Array.isArray(command.values) &&
      command.values.length > 0 &&
      command.values.length <= 192
    ) {
      const values = command.values.map(digit)
      const last = result[result.length - 1]
      if (
        last?.op === 'numbers' &&
        Array.isArray(last.values) &&
        last.values.length > 0 &&
        Object.keys(last).every((field) => ['op', 'values'].includes(field))
      )
        last.values = [...last.values, ...values]
      else result.push({ op: 'numbers', values })
    } else result.push({ ...command })
  }
  return result
}

/** A phrase is one transaction: chart, partial group, cursor and history all commit together. */
export function applyKeywordOperations(
  current: IPerioChart,
  captured: IPerioChart,
  previous: KeywordState,
  input: unknown,
): {
  chart: IPerioChart
  state: KeywordState
  message: string
  focus?: PerioVoiceHighlight
  steps?: KeywordApplicationStep[]
  control?: 'stop' | 'reset' | 'clear_all'
} {
  if (!Array.isArray(input) || input.length > 64)
    throw new Error('Invalid voice response.')
  const chart = {
    ...current,
    perio_data: clonePerioChartData(current.perio_data),
  }
  const state: KeywordState = structuredClone(previous)
  const notes: string[] = []
  let control: 'stop' | 'reset' | 'clear_all' | undefined
  let focus: PerioVoiceHighlight | undefined
  let steps: KeywordApplicationStep[] | undefined
  let groups = 0
  const noPending = (): void => {
    if (state.pending.length)
      throw new Error(
        `Tooth ${state.cursor.tooth} has ${state.pending.length} of 3 readings. Finish the group or say “clear pending” before moving. This phrase was not applied.`,
      )
  }
  const checkUnedited = (cell: Cell): void => {
    if (read(current, cell) !== read(captured, cell))
      throw new Error(
        'This tooth was edited while speech was processing. This phrase was not applied; repeat it.',
      )
  }
  const write = (cell: Cell, after: string): Edit => {
    checkUnedited(cell)
    const before = read(chart, cell)
    chart.perio_data[cell.section][cell.metric][cell.tooth - 1] = after
    return { ...cell, before, after }
  }
  const applySites = (cell: Cell, rawValues: unknown): string => {
    const values = object(rawValues)
    if (
      !Object.keys(values).length ||
      Object.keys(values).some((site) => !sites.includes(site))
    )
      throw new Error('Specify distal, buccal or mesial.')
    const parts = read(chart, cell).split(' ')
    Object.entries(values).forEach(([site, value]) => {
      const index = sites.indexOf(site)
      if (numeric.includes(cell.metric)) parts[index] = String(digit(value))
      else {
        if (typeof value !== 'boolean') throw new Error('Invalid site marker.')
        parts[index] = value ? markers[index] : '0'
      }
    })
    return parts.join(' ')
  }
  const remember = (edits: Edit[]): void => {
    state.history.push({
      cursor: { ...state.cursor },
      ended: state.ended,
      edits,
    })
    state.history = state.history.slice(-200)
  }
  // Stop ends this phrase. Ignore anything recorded after it.
  const stopIndex = input.findIndex((raw) => raw?.op === 'stop')
  const commands = coalesceNumbers(
    stopIndex < 0 ? input : input.slice(0, stopIndex + 1),
  )
  for (const raw of commands) {
    const command = object(raw)
    const { op } = command
    const allowed = ['op']
    if (op === 'numbers') allowed.push('values')
    else if (op === 'unsupported') {
      allowed.push('metric')
      if (
        command.metric !== undefined &&
        !metrics.includes(command.metric as string)
      )
        throw new Error('Unsupported measurement.')
    } else if (op === 'set_many') {
      allowed.push('teeth', 'section', 'metric', 'sites')
      if (command.section !== undefined && !isPerioSection(command.section))
        throw new Error('Specify facial or lingual.')
      if (
        !markerMetrics.includes(command.metric as string) ||
        !Array.isArray(command.teeth) ||
        !command.teeth.length ||
        command.teeth.length > 32 ||
        command.teeth.some(
          (tooth) =>
            typeof tooth !== 'number' ||
            !Number.isInteger(tooth) ||
            tooth < 1 ||
            tooth > 32,
        )
      )
        throw new Error('Invalid bulk marker command.')
    } else if (op === 'jump' || op === 'set') {
      allowed.push('tooth', 'section', 'metric')
      if (op === 'set')
        allowed.push(command.metric === 'Mobility' ? 'value' : 'sites')
      if (
        command.tooth !== undefined &&
        (typeof command.tooth !== 'number' ||
          !Number.isInteger(command.tooth) ||
          command.tooth < 1 ||
          command.tooth > 32)
      )
        throw new Error('Use a Universal tooth number from 1 to 32.')
      if (command.section !== undefined && !isPerioSection(command.section))
        throw new Error('Specify facial or lingual.')
      if (
        command.metric !== undefined &&
        !(op === 'jump' ? numeric : metrics).includes(command.metric as string)
      )
        throw new Error('Unsupported measurement.')
    }
    if (Object.keys(command).some((field) => !allowed.includes(field)))
      throw new Error(
        'Invalid fields in voice command. This phrase was not applied.',
      )
    switch (op) {
      case 'stop':
        state.pending = []
        delete state.pendingBefore
        state.paused = false
        control = 'stop'
        notes.push(
          'Microphone stopped. Unfinished and queued speech discarded. Click the microphone to start again.',
        )
        break
      case 'reset':
      case 'clear_all': {
        if (input.length !== 1)
          throw new Error(
            'Say “reset” or “clear all” as a separate phrase. This phrase was not applied.',
          )
        if (op === 'clear_all') {
          const empty = createEmptyPerioChartData()
          const edits: Edit[] = []
          for (const section of PERIO_SECTIONS) {
            for (const metric of Object.keys(empty[section]) as Array<
              keyof PerioChartSectionData
            >) {
              for (let tooth = 1; tooth <= 32; tooth++) {
                const cell = { section, metric, tooth }
                checkUnedited(cell)
                const value = empty[section][metric][tooth - 1]
                if (read(chart, cell) !== value) edits.push(write(cell, value))
              }
            }
          }
          if (edits.length) remember(edits)
        }
        state.cursor = {
          tooth: 1,
          section: PerioSection.Facial,
          metric: 'PD',
        }
        state.pending = []
        delete state.pendingBefore
        state.ended = false
        state.paused = false
        control = op
        notes.push(
          op === 'clear_all'
            ? 'Active Perio chart cleared on both sides. Say “undo” to restore the cleared values. Listening at tooth 1 facial, PD.'
            : 'Voice entry reset to tooth 1 facial, PD. Existing chart readings kept. Listening.',
        )
        notes.push('Unfinished and queued speech discarded.')
        break
      }
      case 'numbers': {
        if (
          !Array.isArray(command.values) ||
          !command.values.length ||
          command.values.length > 192
        )
          throw new Error('Invalid number sequence.')
        // Validate even ignored readings so a malformed response never partly commits.
        const values = command.values.map(digit)
        if (state.paused) {
          notes.push('Readings ignored while paused.')
          break
        }
        if (
          state.cursor.metric !== 'Mobility' &&
          state.pending.length > 0 &&
          values.length % 3 === 0
        ) {
          throw new Error(
            `Unfinished readings “${state.pending.join(' ')}” are waiting for tooth ${state.cursor.tooth}. ` +
              'Adding these full triplets would shift them across teeth. ' +
              `Finish with ${3 - state.pending.length} missing reading(s), or say “clear pending” ` +
              'before repeating the full group. This phrase was not applied.',
          )
        }
        for (const value of values) {
          if (state.ended)
            throw new Error(
              'Reached tooth 32 lingual. Say “jump to tooth …” to choose the next position. This phrase was not applied.',
            )
          if (!state.pending.length) {
            checkUnedited(state.cursor)
            state.pendingBefore = read(chart, state.cursor)
          }
          state.pending.push(value)
          const size = state.cursor.metric === 'Mobility' ? 1 : 3
          if (state.pending.length === size) {
            if (read(chart, state.cursor) !== state.pendingBefore)
              throw new Error(
                'This tooth changed while a group was unfinished. Say “clear pending”, then repeat the group.',
              )
            remember([
              write(
                state.cursor,
                toStorageOrder(
                  state.cursor.tooth,
                  state.pending.map(String),
                ).join(' '),
              ),
            ])
            state.pending = []
            delete state.pendingBefore
            groups++
            const next = shiftCursor(state.cursor, 1)
            if (!next) state.ended = true
            else {
              if (next.section !== state.cursor.section) {
                notes.push(
                  next.section === PerioSection.Lingual
                    ? 'Facial complete; continuing on lingual.'
                    : 'Upper lingual complete; continuing on lower facial.',
                )
              }
              state.cursor = next
            }
          }
        }
        break
      }
      case 'pause':
        state.paused = true
        notes.push('Paused. The microphone stays on for “resume”.')
        break
      case 'resume':
        state.paused = false
        notes.push('Resumed.')
        break
      case 'clear_pending':
        state.pending = []
        delete state.pendingBefore
        notes.push('Unfinished readings cleared.')
        break
      case 'jump':
        if (Object.keys(command).length === 1)
          throw new Error('Say a tooth, side or measurement after “jump”.')
        noPending()
        if (command.tooth !== undefined)
          state.cursor.tooth = command.tooth as number
        if (command.section !== undefined)
          state.cursor.section = command.section as VoiceCursor['section']
        if (command.metric !== undefined)
          state.cursor.metric = command.metric as VoiceCursor['metric']
        // A metric-only change at the endpoint does not reopen lingual tooth 32.
        if (command.tooth !== undefined || command.section !== undefined)
          state.ended = false
        notes.push('Cursor moved.')
        break
      case 'next':
      case 'back': {
        noPending()
        const next = shiftCursor(state.cursor, op === 'next' ? 1 : -1)
        if (!next)
          throw new Error(
            'No tooth in that direction. Say “jump to tooth …”. This phrase was not applied.',
          )
        state.cursor = next
        state.ended = false
        notes.push('Cursor moved.')
        break
      }
      case 'set': {
        const metric = command.metric as keyof PerioChartSectionData
        if (!metrics.includes(metric))
          throw new Error('Name a supported measurement.')
        const cell: Cell = {
          tooth: (command.tooth as number | undefined) ?? state.cursor.tooth,
          section:
            (command.section as VoiceCursor['section'] | undefined) ??
            state.cursor.section,
          metric,
        }
        let after: string
        if (metric === 'Mobility') after = String(digit(command.value))
        else after = applySites(cell, command.sites)
        if (state.paused) {
          notes.push('Readings ignored while paused.')
          break
        }
        if (
          state.pending.length &&
          cell.tooth === state.cursor.tooth &&
          cell.section === state.cursor.section &&
          cell.metric === state.cursor.metric
        )
          noPending()
        remember([write(cell, after)])
        focus = cell
        notes.push(`${metric} updated on tooth ${cell.tooth} ${cell.section}.`)
        break
      }
      case 'set_many': {
        const metric = command.metric as keyof PerioChartSectionData
        const section =
          (command.section as VoiceCursor['section'] | undefined) ??
          state.cursor.section
        const teeth = Array.from(new Set(command.teeth as number[]))
        if (state.paused) {
          notes.push('Readings ignored while paused.')
          break
        }
        const edits: Edit[] = []
        const bulkSteps: KeywordApplicationStep[] = []
        teeth.forEach((tooth) => {
          const cell: Cell = { tooth, section, metric }
          edits.push(write(cell, applySites(cell, command.sites)))
          bulkSteps.push({
            chart: {
              ...chart,
              perio_data: clonePerioChartData(chart.perio_data),
            },
            tooth,
            section,
            metric,
          })
        })
        remember(edits)
        state.cursor = {
          ...state.cursor,
          tooth: teeth[teeth.length - 1],
          section,
        }
        focus = {
          tooth: teeth[teeth.length - 1],
          section,
          metric,
        }
        steps = bulkSteps
        notes.push(
          `${metric} updated on ${teeth.length} teeth in the ${section} section.`,
        )
        break
      }
      case 'undo': {
        if (state.pending.length) {
          state.pending = []
          delete state.pendingBefore
          notes.push(
            'Unfinished readings cleared. Say “undo” again for the last chart entry.',
          )
          break
        }
        const last = state.history.pop()
        if (!last) {
          notes.push('No voice entry to undo in this session.')
          break
        }
        for (const edit of last.edits) {
          if (read(chart, edit) !== edit.after)
            throw new Error(
              'The last voice entry has since been edited. Undo was not applied.',
            )
          write(edit, edit.before)
        }
        state.cursor = last.cursor
        state.ended = last.ended
        notes.push('Last voice entry undone.')
        break
      }
      case 'unsupported':
        if (
          ['Bleeding', 'Calculus', 'Supp', 'Furcation'].includes(
            command.metric as string,
          )
        )
          throw new Error(
            `${command.metric} uses present/absent site markers. ` +
              `Say “${command.metric} on distal” or “${command.metric} on all sites”. ` +
              'Numeric readings or grades are not supported for this field. This phrase was not applied.',
          )
        throw new Error(
          'That command is not supported. Use numbers, pause, resume, stop, reset, clear all, jump, next, back, undo, or clear pending. This phrase was not applied.',
        )
      default:
        throw new Error('Unknown voice command. This phrase was not applied.')
    }
  }
  if (groups)
    notes.unshift(`${groups} ${groups === 1 ? 'tooth' : 'teeth'} charted.`)
  if (state.pending.length)
    notes.push(
      `Waiting for ${3 - state.pending.length} more ${3 - state.pending.length === 1 ? 'reading' : 'readings'}; ${state.pending.join(' ')} is not saved yet.`,
    )
  if (state.ended && control !== 'stop')
    notes.push(
      'Tooth 32 lingual complete. Choose the next tooth and side with “jump”.',
    )
  if (state.paused)
    notes.push('Chart entry is paused. Say “resume” before entering readings.')
  return {
    chart,
    state,
    message: notes.join(' ') || 'No chart command heard.',
    ...(focus ? { focus } : {}),
    ...(steps ? { steps } : {}),
    ...(control ? { control } : {}),
  }
}

/** Update queued capture baselines for our own writes, while retaining manual-edit conflicts. */
export function rebaseVoiceCapture(
  captured: IPerioChart,
  before: IPerioChart,
  after: IPerioChart,
): IPerioChart {
  const rebased = {
    ...captured,
    perio_data: clonePerioChartData(captured.perio_data),
  }
  for (const section of PERIO_SECTIONS) {
    for (const metric of Object.keys(after.perio_data[section]) as Array<
      keyof PerioChartSectionData
    >) {
      for (let tooth = 1; tooth <= 32; tooth++) {
        const cell = { tooth, section, metric }
        if (
          read(before, cell) !== read(after, cell) &&
          read(captured, cell) === read(before, cell)
        )
          rebased.perio_data[section][metric][tooth - 1] = read(after, cell)
      }
    }
  }
  return rebased
}
